/**
 * Search component used in the Ideal Fit tab
 */

import { useCallback, useContext, useEffect } from "react";
import { JobsContext, MatchContext } from "context/providers";
import { useForm } from "react-hook-form";
import { debounce } from "lodash";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/ui/form";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import HelpText from "components/HelpText";
import { X } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";
import { convertMilesToMeters, getGroupedSkillsByCategory } from "utils/common";
import { IDEAL_FIT_SEARCH_TYPES, TK_SKILL_CATEGORY_MAP } from "lookup";
import { cn } from "utils";

// in meters
const DEFAULT_GEO_DISTANCE = convertMilesToMeters(50);

// Label in miles, actual value in meters
const GEO_DISTANCES = [10, 25, 50, 100, 250].map((d) => ({
  label: d.toString(),
  value: convertMilesToMeters(d),
}));

function SkillFilter({ skills = [], fieldName, label, formControl }) {
  if (skills.length === 0) {
    return (
      <>
        <FormLabel className="text-sm font-semibold mb-4">{label}</FormLabel>
        <p className="text-slate-300 text-sm">
          There are no {label} for this job
        </p>
      </>
    );
  }

  const skillsByCategory = getGroupedSkillsByCategory(skills);

  return (
    <>
      <FormLabel className="text-sm font-semibold mb-4">{label}</FormLabel>

      {Object.keys(skillsByCategory).map((category) => (
        <div key={category}>
          <FormLabel className="text-sm  mb-4">
            {TK_SKILL_CATEGORY_MAP[category]}
          </FormLabel>

          <div className="flex gap-2 flex-wrap">
            {skillsByCategory[category].map((skill) => (
              <FormField
                key={skill.id}
                control={formControl}
                name={fieldName}
                render={({ field }) => (
                  <FormItem key={skill.id}>
                    <FormControl>
                      <Badge
                        className={cn(
                          "cursor-pointer border border-blue-300 pr-1 hover:bg-sky-400 hover:text-white",
                          {
                            "text-white bg-sky-600": field.value?.includes(
                              skill.id
                            ),
                            "text-[#0EA5E9] bg-white": !field.value?.includes(
                              skill.id
                            ),
                          }
                        )}
                        variant={
                          field.value?.includes(skill.id)
                            ? undefined
                            : "outline"
                        }
                        onClick={() => {
                          return field.value?.includes(skill.id)
                            ? field.onChange(
                                field.value.filter(
                                  (value) => value !== skill.id
                                )
                              )
                            : field.onChange([
                                ...(field.value || []),
                                skill.id,
                              ]);
                        }}
                      >
                        {skill.name}{" "}
                        {field.value?.includes(skill.id) && (
                          <X className="w-4 h-4" />
                        )}
                      </Badge>
                    </FormControl>
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export default function IdealFitFilter() {
  const { jobOpp } = useContext(JobsContext);
  const { fetchIdealCandidates } = useContext(MatchContext);

  const form = useForm({
    defaultValues: {
      requiredSkills: jobOpp?.TKSkills?.map((s) => s.id) || [],
      optionalSkills: jobOpp?.optionalTKSkills?.map((s) => s.id) || [],
      geoDistance: DEFAULT_GEO_DISTANCE,
      basedOnWorkHistory: false,
      searchType: IDEAL_FIT_SEARCH_TYPES.ADVANCED,
    },
  });

  const fetchIdealCandidatesDebounce = debounce(async function (data) {
    await fetchIdealCandidates(data);
  }, 750);

  const fetchIdealCandidatesCall = useCallback((data) => {
    fetchIdealCandidatesDebounce(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = async (data) => {
    // If we don't pass skills to override for fetching ideal candidates,
    // we read the skills from the job itself
    let requiredSkillsToOverride;
    let optionalSkillsToOverride;
    let geoDistance;
    if (jobOpp?.TKSkills?.length !== data.requiredSkills.length) {
      requiredSkillsToOverride = data.requiredSkills.map((ds) => {
        const skill = jobOpp.TKSkills.find((js) => js.id === ds);

        return skill;
      });
    }

    if (jobOpp?.optionalTKSkills?.length !== data.optionalSkills.length) {
      optionalSkillsToOverride = data.optionalSkills.map((ds) => {
        const skill = jobOpp.optionalTKSkills.find((js) => js.id === ds);

        return skill;
      });
    }

    if (jobOpp?.location?.locationId) {
      geoDistance = data.geoDistance;
    }

    await fetchIdealCandidatesCall({
      requiredSkills: requiredSkillsToOverride,
      optionalSkills: optionalSkillsToOverride,
      geoDistance,
      basedOnWorkHistory: data.basedOnWorkHistory,
      searchType: data.searchType,
    });
  };

  // Submit the form when a value in the form changes
  // There's no explicit submit button
  useEffect(() => {
    const subscription = form.watch((values) => onChange(values));

    return () => subscription.unsubscribe();
  });

  const byWorkExperienceIsEnabled = form.getValues()?.basedOnWorkHistory;

  return (
    <section className="p-4">
      <p className="uppercase text-base text-gray-400 font-bold mb-4">
        Ideal Fit Customization
      </p>
      <Form {...form}>
        <form className="flex flex-col space-y-4">
          <FormField
            control={form.control}
            name="searchType"
            render={({ field }) => (
              <FormItem
                className={cn("border border-dashed p-4", {
                  "opacity-50": byWorkExperienceIsEnabled,
                })}
              >
                <div className="mb-4">
                  <FormLabel className="uppercase text-sm font-semibold mb-4 text-indigo-500">
                    Search Type
                  </FormLabel>
                </div>
                <FormControl>
                  <RadioGroup
                    disabled={byWorkExperienceIsEnabled}
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-col space-x-1 items-baseline"
                  >
                    <FormItem className="flex items-center space-x-3 space-y-0">
                      <FormControl>
                        <RadioGroupItem value={IDEAL_FIT_SEARCH_TYPES.BASIC} />
                      </FormControl>
                      <FormLabel className="font-semibold">
                        {IDEAL_FIT_SEARCH_TYPES.BASIC}
                      </FormLabel>
                    </FormItem>

                    <FormItem className="flex items-center space-x-3 space-y-0">
                      <FormControl>
                        <RadioGroupItem
                          value={IDEAL_FIT_SEARCH_TYPES.ADVANCED}
                        />
                      </FormControl>
                      <FormLabel className="font-semibold">
                        {IDEAL_FIT_SEARCH_TYPES.ADVANCED}
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <section className="border border-dashed space-y-4 px-4">
            <FormField
              control={form.control}
              name="basedOnWorkHistory"
              render={({ field }) => (
                <FormItem>
                  <div className="flex gap-2 items-center mt-4 h-4">
                    <FormLabel className="capitalize">
                      Search Resume Experience
                    </FormLabel>
                    <Input
                      onChange={field.onChange}
                      type="checkbox"
                      className="w-3.5 h-3.5 inline-block border-2 border-gray-300 rounded mr-2 checked:bg-blue-600 checked:border-transparent"
                    />
                    <HelpText
                      text="No candidates found? Explore candidates through their past roles and experience."
                      className="ml-2"
                      toolTipClassName="!w-48 md:!w-max"
                    />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="requiredSkills"
              render={() => (
                <FormItem className="!space-y-0">
                  <div className="mb-4">
                    <FormLabel className="uppercase text-sm font-semibold mb-4 text-indigo-500">
                      Skills
                    </FormLabel>
                  </div>

                  <SkillFilter
                    skills={jobOpp?.TKSkills || []}
                    label="Required Skills"
                    fieldName="requiredSkills"
                    formControl={form.control}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <hr className="border-b-2 w-full" />

            <FormField
              control={form.control}
              name="optionalSkills"
              render={() => (
                <FormItem className="pb-4">
                  <SkillFilter
                    skills={jobOpp?.optionalTKSkills || []}
                    label="Optional Skills"
                    fieldName="optionalSkills"
                    formControl={form.control}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>

          {jobOpp?.location?.locationId && (
            <FormField
              control={form.control}
              name="geoDistance"
              render={({ field }) => (
                <FormItem className="border border-dashed p-4">
                  <div className="mb-4">
                    <FormLabel className="uppercase text-sm font-semibold mb-4 text-indigo-500">
                      Proximity Radius
                    </FormLabel>
                    <FormDescription>
                      {`${jobOpp.location.cityName}, ${jobOpp.location.stateName}, ${jobOpp.location.countryName}`}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-x-1 items-baseline"
                    >
                      {GEO_DISTANCES.map((gd) => (
                        <FormItem
                          className="flex items-center space-x-3 space-y-0"
                          key={gd.value}
                        >
                          <FormControl>
                            <RadioGroupItem value={gd.value} />
                          </FormControl>
                          <FormLabel className="font-semibold">
                            {gd.label} miles
                          </FormLabel>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </form>
      </Form>
    </section>
  );
}
